// ─────────────────────────────────────────────────────────────────────────────
// 🔑 SIGNING POPUPS: ONE SCROLLER, AND "READ TO THE END" BY A SENTINEL (fix/signing-scroll, Jesse 2026-11-02 — Hannah's
// iPhone on OTA staging a04ac312: the agreement box would not scroll by touch at first, only the whole popup did; and
// the popup scrolled sideways).
//
// · useInlineDocument() — true on a phone-width screen (≤ 639px, the breakpoint OfferAcceptedReview already uses) and
//   ALWAYS in the native app. There, a signing popup has no inner scroll box: the document renders inline in the
//   popup's single vertically scrolling body (index.css .safe-modal-body). A nested scroller inside the modal's
//   scroller is what took (or lost) the first touch gesture. Desktop keeps its inner box.
// · useScrolledToEnd(root, sentinel, onEnd, active) — fires onEnd ONCE when the sentinel (an empty element placed at
//   the very end of the document) comes into view inside `root` (the element that actually scrolls: the modal body
//   inline, the inner box on desktop). An IntersectionObserver, never scrollTop arithmetic: it does not care which
//   element scrolled, how tall the content became after it loaded, or how a touch gesture was delivered.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from 'react';
import { isNativeApp } from '../utils/isNativeApp';

export const PHONE_QUERY = '(max-width: 639px)';

export function useInlineDocument() {
  const native = isNativeApp();
  const get = () => native || (typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(PHONE_QUERY).matches);
  const [inline, setInline] = useState(get);
  useEffect(() => {
    if (native || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(PHONE_QUERY);
    const on = () => setInline(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, [native]);
  return inline;
}

export function useScrolledToEnd(root, sentinel, onEnd, active = true) {
  const cb = useRef(onEnd);
  cb.current = onEnd;
  useEffect(() => {
    if (!active || !root || !sentinel || typeof IntersectionObserver === 'undefined') return undefined;
    // 'armed' → fires on the sentinel coming into view; 'busy' while onEnd runs; 'done' after it succeeded. If onEnd
    // rejects (the read record could not be saved), it re-arms only after the sentinel has LEFT the view, so the
    // borrower scrolls to the end again — never a retry loop while the end stays on screen.
    let phase = 'armed';
    const io = new IntersectionObserver((entries) => {
      const seen = entries.some((e) => e.isIntersecting);
      if (phase === 'waitAway') { if (!seen) phase = 'armed'; return; }
      if (phase !== 'armed' || !seen) return;
      phase = 'busy';
      Promise.resolve().then(() => cb.current && cb.current())
        .then(() => { phase = 'done'; io.disconnect(); })
        .catch(() => { phase = 'waitAway'; });
    }, { root, threshold: 0 });
    io.observe(sentinel);
    return () => io.disconnect();
  }, [root, sentinel, active]);
}
